import { Link } from "react-router-dom";
import { ArrowLeft, ArrowUpRight } from "lucide-react";
import AnchorMark from "@/components/AnchorMark";
import CalendlyCTA from "@/components/CalendlyCTA";
import Navbar from "@/components/sections/Navbar";
import Footer from "@/components/sections/Footer";

// Reusable section heading
const Section = ({ overline, n, title, children }) => (
  <section className="mb-24 md:mb-32" data-testid={`story-section-${n}`}>
    <div className="flex items-baseline justify-between mb-8">
      <p className="overline">{overline}</p>
      <span
        className="serif text-sm tracking-[0.2em] text-[#8A8D93]"
        style={{ fontWeight: 300 }}
      >
        {n}
      </span>
    </div>
    {title && (
      <h2
        className="serif text-3xl md:text-4xl text-[#15243D] leading-[1.15] mb-10 max-w-2xl"
        style={{ fontWeight: 300 }}
      >
        {title}
      </h2>
    )}
    <div className="space-y-5 text-lg font-light leading-relaxed text-[#5A5D63]">
      {children}
    </div>
  </section>
);

const Divider = () => (
  <div className="flex items-center justify-center my-20" aria-hidden>
    <span className="w-12 h-px bg-[#15243D]/15" />
    <AnchorMark size={20} color="#15243D" className="mx-4 opacity-30" />
    <span className="w-12 h-px bg-[#15243D]/15" />
  </div>
);

const Q = ({ children }) => (
  <blockquote
    className="serif italic text-2xl md:text-3xl text-[#15243D] leading-snug border-l-2 border-[#C07865] pl-6 py-1 my-8"
    style={{ fontWeight: 300 }}
  >
    {children}
  </blockquote>
);

const Lead = ({ children }) => (
  <p className="text-xl md:text-2xl font-light text-[#15243D] leading-relaxed">
    {children}
  </p>
);

export default function StoryPage() {
  return (
    <div
      data-testid="story-page"
      className="min-h-screen"
      style={{ backgroundColor: "#F7F5F0" }}
    >
      <Navbar />

      <main className="pt-32 md:pt-40 pb-24 md:pb-32">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          {/* Back link */}
          <Link
            to="/"
            data-testid="story-back-link"
            className="inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase text-[#5A5D63] hover:text-[#C07865] transition-colors mb-16"
          >
            <ArrowLeft size={14} strokeWidth={1.5} />
            Return home
          </Link>

          {/* Cover */}
          <header className="text-center mb-24 md:mb-32">
            <div className="flex justify-center mb-10">
              <AnchorMark size={72} color="#15243D" />
            </div>
            <p className="overline mb-6">The full story</p>
            <h1
              className="serif text-5xl md:text-7xl text-[#15243D] leading-[1.04] tracking-tight mb-8"
              style={{ fontWeight: 300 }}
            >
              How <em className="italic text-[#C07865]">Anchor</em><br />
              came to be.
            </h1>
            <p
              className="serif italic text-lg md:text-xl text-[#5A5D63] max-w-md mx-auto"
              style={{ fontWeight: 300 }}
            >
              A practice that began long before there was a name for it.
            </p>
          </header>

          {/* Origin */}
          <Section overline="I — The season" n="01 / 09" title="The season that stripped away every illusion of control.">
            <p>
              Anchor was born during a season when life stripped away every
              illusion of control.
            </p>
            <p>
              I was navigating business collapse, financial pressure, distance
              from my sons, caregiving for aging parents, family tension, and
              the uncertainty of starting over. On the outside, it looked like
              survival. But underneath it, something deeper was happening.
            </p>
            <p>
              I began to notice that the real problem was not only what was
              happening around me. It was how easily people — including myself
              — could get pulled out of themselves by pressure, fear, conflict,
              grief, or uncertainty.
            </p>
            <Q>That became the spark.</Q>
            <p>
              Anchor came from lived experience, not theory. It came from long
              drives, quiet mornings, hard conversations, caregiving moments,
              restaurant shifts, financial stress, and the practice of
              returning to presence when life felt overwhelming.
            </p>
            <p>The need it aimed to fulfill was simple:</p>
            <Lead>
              People do not need more information. They need a way to come back
              to themselves.
            </Lead>
          </Section>

          <Divider />

          {/* Who it's for */}
          <Section
            overline="II — Who it’s for"
            n="02 / 09"
            title="The people who appear to be handling everything."
          >
            <p>
              Anchor was created for people who feel stretched thin, reactive,
              overwhelmed, or disconnected from their own center.
            </p>
            <p>
              They wake up already thinking about everything that could go
              wrong. They carry stress from work into their relationships. They
              replay conversations long after they’ve ended. They live in a
              constant state of tension without realizing it has become their
              normal.
            </p>
            <p>Over time, this creates a subtle drift.</p>
            <p>
              The people who experience this are often the ones who appear to
              be handling everything.
            </p>
            <ul className="space-y-2 pl-1">
              {[
                "Business owners",
                "Professionals",
                "Parents",
                "Caregivers",
                "Leaders",
                "People navigating major transitions",
              ].map((i) => (
                <li
                  key={i}
                  className="flex items-baseline gap-3 text-[#15243D]"
                >
                  <span className="text-[#C07865] text-xs">—</span>
                  {i}
                </li>
              ))}
            </ul>
            <p>
              From the outside, they look capable. Inside, they feel
              overwhelmed, exhausted, frustrated, anxious, scattered, or stuck.
            </p>
            <Q>
              Most have spent years trying to think their way out of problems
              that cannot be solved by thinking alone.
            </Q>
          </Section>

          <Divider />

          {/* The Practice Began */}
          <Section
            overline="III — The practice"
            n="03 / 09"
            title="Anchor didn’t begin as a program. It began as a way of living."
          >
            <p>
              Long before there was a framework, a book, a program, or a
              business, there was simply a practice of returning to the present
              moment in the middle of difficult circumstances.
            </p>
            <p>
              The first real proof came during one of the hardest seasons of my
              life.
            </p>
            <p>
              I had left Seattle and driven across the country to help care for
              my aging parents. My business was unraveling. Financial pressure
              was mounting. I was separated from my sons. Every day seemed to
              bring another problem demanding attention.
            </p>
            <p>By all appearances, I should have been overwhelmed.</p>
            <p>And sometimes I was.</p>
            <p>But I began noticing something.</p>
            <Lead>
              The circumstances weren’t improving overnight, yet my experience
              of them was changing.
            </Lead>
            <p>The problems didn’t disappear. The space around them grew.</p>
            <p>
              Instead of getting pulled into every fear, every story, every
              emotional wave, I started practicing a simple return: pause,
              breathe, come back to what is actually happening right now. Again
              and again.
            </p>
          </Section>

          <Divider />

          {/* The Stairs */}
          <Section
            overline="IV — One moment"
            n="04 / 09"
            title="A coal furnace, a tightening jaw, a choice."
          >
            <p>
              My father, struggling with Parkinson’s, was having difficulty
              navigating the stairs to tend the coal furnace that heated the
              house. There was frustration, fear, tension, and years of family
              dynamics wrapped up in what seemed like a simple task.
            </p>
            <p>
              In the past, I would have been pulled into the emotional charge
              of the moment. Instead, I noticed my jaw tightening. I noticed my
              breathing. I paused.
            </p>
            <p>
              And rather than reacting to everything I was thinking and
              feeling, I responded to what was actually needed.
            </p>
            <Q>
              The situation itself wasn’t extraordinary. What was extraordinary
              was the realization that presence created options that reactivity
              could never see.
            </Q>
            <p>That became one of the foundational insights behind Anchor.</p>
            <p>
              The milestone wasn’t a dramatic breakthrough. It was the
              discovery that life could remain difficult while I remained
              grounded.
            </p>
          </Section>

          <Divider />

          {/* Doubt */}
          <Section
            overline="V — The doubt"
            n="05 / 09"
            title="The challenge wasn’t convincing other people. It was convincing myself."
          >
            <p>
              Like many people, I initially believed the answer had to be more
              complicated. A better strategy. More information. A breakthrough
              insight.
            </p>
            <p>
              Yet the deeper I looked, the more I realized that most people’s
              suffering wasn’t coming from a lack of knowledge. It was coming
              from a lack of awareness.
            </p>
            <Lead>The gap isn’t knowledge. The gap is practice.</Lead>
            <p className="pt-2">
              <span className="serif italic text-xl text-[#15243D]">
                “This sounds too simple.”
              </span>{" "}
              Most people hear pause, breathe, notice, return, and think{" "}
              <em>that can’t be enough</em>.
            </p>
            <p>
              The challenge wasn’t creating complexity. The challenge was
              creating a practice simple enough to be used in the middle of a
              difficult conversation, a stressful workday, a family conflict,
              or a personal crisis.
            </p>
            <p className="pt-4">
              <span className="serif italic text-xl text-[#15243D]">
                “If I slow down, won’t I fall behind?”
              </span>{" "}
              I believed that too.
            </p>
            <p>
              What I discovered was the opposite. The clearer and more grounded
              I became, the better my decisions got. I wasted less energy
              fighting reality. I spent less time trapped in mental loops. I
              recovered faster when life knocked me off balance.
            </p>
            <Q>Slowing down wasn’t weakness. It was leverage.</Q>
          </Section>

          <Divider />

          {/* The Framework */}
          <Section
            overline="VI — Same signal"
            n="06 / 09"
            title="Different source. Same signal."
          >
            <p>
              The surface details change. The feeling underneath often doesn’t.
            </p>
            <p>
              One day it’s a difficult coworker. Another day it’s a family
              disagreement. Another day it’s financial stress.
            </p>
            <p>
              Different source. Same signal. The same tension. The same fear.
              The same urge to react.
            </p>
            <p>
              Once people see that pattern, life becomes much easier to
              understand. They stop chasing every individual problem and start
              learning how to work with the underlying experience itself.
            </p>
            <Lead>At its simplest, Anchor teaches one thing:</Lead>
            <div className="flex flex-wrap items-baseline gap-x-6 gap-y-3 py-2">
              {["Notice.", "Pause.", "Return.", "Respond."].map((w) => (
                <span
                  key={w}
                  className="serif italic text-3xl md:text-4xl text-[#C07865]"
                  style={{ fontWeight: 300 }}
                >
                  {w}
                </span>
              ))}
            </div>
            <p>That’s it.</p>
            <p>
              Of course there is depth beneath those four words. But people
              don’t need to understand the entire ocean before they can learn
              how to swim. They simply need a place to begin.
            </p>
          </Section>

          <Divider />

          {/* What Changes */}
          <Section
            overline="VII — What changes"
            n="07 / 09"
            title="The waves still come. You no longer get swept away by every one of them."
          >
            <p>People often describe the experience in surprisingly simple ways:</p>
            <ul className="space-y-3 pl-1">
              {[
                "I don’t spiral like I used to.",
                "I don’t carry things as long.",
                "I feel more like myself.",
                "I trust my decisions.",
                "I feel grounded.",
                "I have space again.",
              ].map((i) => (
                <li
                  key={i}
                  className="flex items-baseline gap-3 text-[#15243D] serif italic text-xl leading-snug"
                  style={{ fontWeight: 300 }}
                >
                  <span className="text-[#C07865] text-xs not-italic">—</span>
                  “{i}”
                </li>
              ))}
            </ul>
            <p className="pt-4">
              People stop waiting for life to calm down before they allow
              themselves to feel calm. They stop postponing peace until
              circumstances improve.
            </p>
            <Q>
              Groundedness is not something life gives you. It’s something you
              cultivate regardless of what life is doing.
            </Q>
          </Section>

          <Divider />

          {/* The Ethic */}
          <Section
            overline="VIII — The ethic"
            n="08 / 09"
            title="You are not broken. You are not weak. You are not behind."
          >
            <p>
              You have simply never been taught how to return to yourself when
              life pulls you away.
            </p>
            <p>
              Anchor does not promise a perfect life. It does not sell escape,
              certainty, or control. It teaches people how to meet life as it
              is — with awareness, with presence, with honesty, with courage.
            </p>
            <p>
              The trust comes from that honesty. Anchor does not say,{" "}
              <em>we will fix your life</em>. It says:
            </p>
            <Lead>
              Life will bring waves. Anchor teaches you how not to become the
              storm.
            </Lead>
          </Section>

          <Divider />

          {/* Closing */}
          <Section overline="IX — The line" n="09 / 09" title={null}>
            <div className="text-center py-8">
              <div className="flex justify-center mb-12">
                <AnchorMark size={56} color="#C07865" />
              </div>
              <p
                className="serif text-4xl md:text-5xl text-[#15243D] leading-[1.1]"
                style={{ fontWeight: 300 }}
              >
                Strength begins in <em className="italic text-[#C07865]">stillness</em>.
              </p>
            </div>
          </Section>

          {/* CTA at end */}
          <div className="mt-20 pt-16 border-t border-[#15243D]/10 grid grid-cols-1 md:grid-cols-2 gap-8 items-start">
            <div>
              <p className="overline mb-4">Begin the practice</p>
              <p className="text-base font-light text-[#5A5D63] mb-6 leading-relaxed">
                The 28-day program is freely available — one short reflection
                each day. Read it, listen if you have time, and let it begin to
                change the way you meet your hours.
              </p>
              <Link
                to="/program"
                data-testid="story-program-button"
                className="btn-secondary"
              >
                Open the 28-day program
                <ArrowUpRight size={16} strokeWidth={1.5} />
              </Link>
            </div>
            <div>
              <CalendlyCTA
                testId="story-calendly"
                label="Talk with Ben"
                subtitle="A quiet 30 minutes. No pitch. No pressure."
              />
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
